import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  forwardRef,
  lazy,
  Suspense,
} from "react";
import type ReactQuillType from "react-quill";
import { MessageInputProps } from "./MessageInput.types";
import {
  MicWrapper,
  StyledInput,
  StyledQuill,
  ToolbarSeparator,
} from "./MessageInput.styles";
import { Icon } from "../../../components/Icon/Icon";
import { colorPalette } from "../../../theme/colors";
import FlexColumn from "../../../components/Layout/FlexColumn/FlexColumn";
import FlexRow from "../../../components/Layout/FlexRow/FlexRow";
import Box from "../../../primitives/Box/Box";
import { AudioRecorderWaveform } from "../AudioRecorderWaveform/AudioRecorderWaveform";
import { Body5RegularTypography } from "../../../primitives/Typography/typographyVariants";
import { AudioPlayer } from "../AudioPlayer/AudioPlayer";

// Lazy load ReactQuill to keep it out of the main bundle
const ReactQuill = lazy(() =>
  import(/* webpackChunkName: "ReactQuill" */ "react-quill").then((module) => {
    // Dynamically import the CSS when ReactQuill is loaded
    // @ts-ignore - CSS import
    import("react-quill/dist/quill.snow.css");
    return module;
  })
);

export interface MessageInputRef {
  focus: () => void;
}

export const MessageInput = forwardRef<MessageInputRef, MessageInputProps>(
  (
    {
      type,
      value,
      onChange,
      onSubmit,
      placeholder = "",
      maxLength,
      disabled = false,
      keyboardType = "default",
      dataCy = "message-input",
      showClearButton = true,
      onClear,
      onClick,
      showToolbar = true,
      showPlusButton = true,
      showPlusNotification = false,
      onPlusClick,
      allowEmptySubmit = false,
      isProcessing = false,
      onFormatClick,
      rightAction = "submit",
      onMicClick,
      onMicPointerDown,
      onMicPointerUp,
      onMicPointerLeave,
      voiceUiState,
      enableHoldToRecord = true,
      holdThresholdMs = 200,
      voiceAudioGroupId,
      voicePlayerId,
      onVoiceStart,
      onVoiceStop,
      onVoiceCancel,
      onVoiceSend,
      onVoicePreviewPlay,
      onVoicePreviewPause,
    },
    ref
  ) => {
    const quillRef = useRef<ReactQuillType | null>(null);
    const textInputRef = useRef<HTMLTextAreaElement>(null);
    const holdTimerRef = useRef<number | null>(null);
    const isPointerDownRef = useRef(false);
    const startedByHoldRef = useRef(false);

    const [formats, setFormats] = useState<Record<string, any>>({});
    const [editorReady, setEditorReady] = useState(false);
    const voiceMode = voiceUiState?.mode ?? "idle";
    const isVoiceRecording = voiceMode === "recording";
    const isVoiceReview = voiceMode === "review";
    const shouldShowVoicePanel = isVoiceRecording || isVoiceReview;

    // Callback ref to detect when ReactQuill becomes available
    const quillCallbackRef = useCallback((instance: ReactQuillType | null) => {
      quillRef.current = instance;
      setEditorReady(instance !== null);
    }, []);
    const previousHeightRef = useRef<number>(32);
    const isScrollingInputRef = useRef<boolean>(false);
    const touchStartYRef = useRef<number>(0);
    const touchStartScrollTopRef = useRef<number>(0);
    useImperativeHandle(
      ref,
      () => ({
        focus: () => {
          if (type === "text" && textInputRef.current) {
            textInputRef.current.focus();
            // iOS-specific handling: click the input directly
            if (
              typeof window !== "undefined" &&
              /iPad|iPhone|iPod/.test(navigator.userAgent)
            ) {
              setTimeout(() => {
                if (textInputRef.current) {
                  textInputRef.current.focus();
                  textInputRef.current.click();
                }
              }, 0);
            }
          } else if (type === "rich" && editorReady && quillRef.current) {
            const editor = quillRef.current.getEditor();
            editor.focus();
            // iOS-specific handling for rich text editor
            if (
              typeof window !== "undefined" &&
              /iPad|iPhone|iPod/.test(navigator.userAgent)
            ) {
              setTimeout(() => {
                const quillElement = editor.root;
                if (quillElement) {
                  quillElement.focus();
                  quillElement.click();
                }
              }, 0);
            }
          }
        },
      }),
      [type, editorReady]
    );

    const handleMicClick = useCallback(() => {
      if (!disabled && !isProcessing) onMicClick?.();
    }, [disabled, isProcessing, onMicClick]);

    const handleTouchStart = useCallback(
      (e: React.TouchEvent) => {
        if (type === "text" && textInputRef.current) {
          touchStartYRef.current = e.touches[0].clientY;
          touchStartScrollTopRef.current = textInputRef.current.scrollTop;
          isScrollingInputRef.current = false;
        }
      },
      [type]
    );

    const handleTouchMove = useCallback(
      (e: React.TouchEvent) => {
        if (type === "text" && textInputRef.current) {
          const touchY = e.touches[0].clientY;
          const deltaY = touchY - touchStartYRef.current;
          const currentScrollTop = textInputRef.current.scrollTop;
          const scrollHeight = textInputRef.current.scrollHeight;
          const clientHeight = textInputRef.current.clientHeight;
          const isAtTop = currentScrollTop === 0;
          const isAtBottom =
            currentScrollTop + clientHeight >= scrollHeight - 1;

          // Check if we're trying to scroll within the textarea
          if (
            (deltaY > 0 && !isAtTop) || // Scrolling down and not at top
            (deltaY < 0 && !isAtBottom) // Scrolling up and not at bottom
          ) {
            isScrollingInputRef.current = true;
            e.stopPropagation(); // Prevent background scroll
          } else if (
            (deltaY > 0 && isAtTop) || // Trying to scroll down but at top
            (deltaY < 0 && isAtBottom) // Trying to scroll up but at bottom
          ) {
            // Allow background scroll only when at boundaries
            isScrollingInputRef.current = false;
          }
        }
      },
      [type]
    );

    const handleTouchEnd = useCallback((e: React.TouchEvent) => {
      if (isScrollingInputRef.current) {
        e.stopPropagation();
      }
      isScrollingInputRef.current = false;
    }, []);

    const dismissKeyboard = useCallback(() => {
      if (type === "rich" && editorReady && quillRef.current) {
        const editor = quillRef.current.getEditor();
        editor.root.blur();
      }
    }, [type, editorReady]);

    const handleClear = useCallback(() => {
      if (onClear) {
        onClear();
      } else {
        if (type === "rich" && editorReady && quillRef.current) {
          const editor = quillRef.current.getEditor();
          editor.setText("");
        }
        onChange("");
      }
    }, [onClear, onChange, type, editorReady]);

    const handlePlusClick = useCallback(() => {
      if (onPlusClick) {
        onPlusClick();
      }
    }, [onPlusClick]);

    const handleTextChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        let newValue = e.target.value.replace(/\r/g, "");

        // Validate input based on keyboardType
        if (keyboardType === "numeric") {
          const numericRegex = /^[0-9.-]*$/;
          if (!numericRegex.test(newValue)) {
            return;
          }
        }

        if (maxLength === undefined || newValue.length <= maxLength) {
          onChange(newValue);

          const textarea = e.target;

          // Check if content actually contains newlines
          const hasNewlines = newValue.includes("\n");

          if (newValue.trim() === "" || newValue === "") {
            // Completely empty - force single line
            textarea.style.height = "32px";
            previousHeightRef.current = 32;
          } else if (hasNewlines) {
            // Content has explicit newlines - use measurement element for accurate height
            const computedStyle = window.getComputedStyle(textarea);
            const measure = document.createElement("div");
            measure.style.position = "absolute";
            measure.style.visibility = "hidden";
            measure.style.height = "auto";
            measure.style.width = `${textarea.clientWidth}px`;
            measure.style.whiteSpace = "pre-wrap";
            measure.style.wordWrap = "break-word";
            measure.style.font = computedStyle.font;
            measure.style.fontSize = computedStyle.fontSize;
            measure.style.fontFamily = computedStyle.fontFamily;
            measure.style.fontWeight = computedStyle.fontWeight;
            measure.style.letterSpacing = computedStyle.letterSpacing;
            measure.style.padding = computedStyle.padding;
            measure.style.boxSizing = computedStyle.boxSizing;
            measure.style.border = computedStyle.border;
            measure.textContent = newValue;

            document.body.appendChild(measure);
            const measuredHeight = measure.offsetHeight;
            document.body.removeChild(measure);

            const finalHeight = Math.min(measuredHeight, 122);
            textarea.style.height = finalHeight + "px";
            previousHeightRef.current = finalHeight;
          } else {
            // No newlines - use measurement element to detect actual wrapping
            const computedStyle = window.getComputedStyle(textarea);
            const lineHeight = parseInt(computedStyle.lineHeight) || 17;

            // Create hidden measurement element
            const measure = document.createElement("div");
            measure.style.position = "absolute";
            measure.style.visibility = "hidden";
            measure.style.height = "auto";
            measure.style.width = `${textarea.clientWidth}px`;
            measure.style.whiteSpace = "pre-wrap";
            measure.style.wordWrap = "break-word";
            measure.style.font = computedStyle.font;
            measure.style.fontSize = computedStyle.fontSize;
            measure.style.fontFamily = computedStyle.fontFamily;
            measure.style.fontWeight = computedStyle.fontWeight;
            measure.style.letterSpacing = computedStyle.letterSpacing;
            measure.style.padding = computedStyle.padding;
            measure.style.boxSizing = computedStyle.boxSizing;
            measure.style.border = computedStyle.border;
            measure.textContent = newValue;

            document.body.appendChild(measure);
            const measuredHeight = measure.offsetHeight;
            document.body.removeChild(measure);

            // Check if measured height indicates wrapping (more than one line)
            const isWrapped = measuredHeight > lineHeight + 10; // Add buffer for measurement differences

            if (isWrapped) {
              // Content actually wrapped - use measured height (includes padding)
              const finalHeight = Math.min(measuredHeight, 122);
              textarea.style.height = finalHeight + "px";
              previousHeightRef.current = finalHeight;
            } else {
              // Single line - force 32px (don't use scrollHeight to avoid Safari bug)
              textarea.style.height = "32px";
              previousHeightRef.current = 32;
            }
          }
        }
      },
      [onChange, maxLength, keyboardType]
    );
    const handleQuillChange = useCallback(
      (content: string, delta: any, source: any, editor: any) => {
        try {
          const plainText = editor.getText().trim();

          if (maxLength !== undefined && plainText.length > maxLength) {
            return;
          }

          const isEmptyContent =
            !content ||
            content === "<p></p>" ||
            content === "<p><br></p>" ||
            content.trim() === "" ||
            plainText === "";

          if (isEmptyContent) {
            onChange("");
          } else {
            onChange(content);
          }
        } catch (error) {
          onChange(content);
        }
      },
      [onChange, maxLength]
    );

    const getCharacterCount = useCallback(() => {
      if (type === "text") {
        return value.length;
      }

      try {
        if (!editorReady || !quillRef.current) return 0;
        const editor = quillRef.current.getEditor();
        return Math.max(0, editor.getLength() - 1);
      } catch (error) {
        return 0;
      }
    }, [type, value, editorReady]);

    const characterCount = getCharacterCount();
    const isOverLimit =
      typeof maxLength === "number" && characterCount > maxLength;
    const isSubmitDisabled =
      disabled ||
      (!allowEmptySubmit && (!value.trim() || characterCount === 0)) ||
      isOverLimit;

    const handleSubmit = useCallback(() => {
      // Update this condition to allow empty submit when allowEmptySubmit is true
      if (
        !disabled &&
        (allowEmptySubmit || value.trim()) &&
        !isOverLimit &&
        !isProcessing
      ) {
        dismissKeyboard();
        onSubmit(value);
      }
    }, [
      disabled,
      value,
      onSubmit,
      dismissKeyboard,
      isOverLimit,
      allowEmptySubmit,
      isProcessing,
    ]);

    const handleFormat = useCallback(
      (format: string) => {
        try {
          if (editorReady && quillRef.current) {
            const editor = quillRef.current.getEditor();
            const isActive = !!formats[format];
            editor.format(format, !isActive);
            setTimeout(() => {
              setFormats(editor.getFormat());
            }, 10);
            if (onFormatClick) {
              onFormatClick(format);
            }
          }
        } catch (error) {
          console.error("Error handling format:", error);
        }
      },
      [formats, editorReady, onFormatClick]
    );

    const handleList = useCallback(
      (listType: string) => {
        try {
          if (editorReady && quillRef.current) {
            const editor = quillRef.current.getEditor();
            const currentList = formats.list;

            if (listType === "checked") {
              if (currentList === "checked") {
                editor.format("list", "unchecked");
              } else if (currentList === "unchecked") {
                editor.format("list", false);
              } else {
                editor.format("list", "checked");
              }
            } else {
              const shouldRemoveList = currentList === listType;
              editor.format("list", shouldRemoveList ? false : listType);
            }

            setTimeout(() => {
              setFormats(editor.getFormat());
            }, 10);
            if (onFormatClick) {
              // Map list types to format names for GA
              const formatName =
                listType === "checked"
                  ? "checked"
                  : listType === "ordered"
                  ? "ordered"
                  : "bullet";
              onFormatClick(formatName);
            }
          }
        } catch (error) {
          console.error("Error handling list:", error);
        }
      },
      [formats, editorReady, onFormatClick]
    );

    const isFormatActive = useCallback(
      (format: string) => {
        return !!formats[format];
      },
      [formats]
    );

    const isListActive = useCallback(
      (listType: string) => {
        return formats.list === listType;
      },
      [formats]
    );

    const hasContent = value.trim().length > 0;

    const clearHoldTimer = () => {
      if (holdTimerRef.current != null) {
        window.clearTimeout(holdTimerRef.current);
        holdTimerRef.current = null;
      }
    };

    const handleMicPointerDownInternal = useCallback(
      (e: React.PointerEvent) => {
        if (disabled || isProcessing) return;
        if (!enableHoldToRecord) {
          onMicPointerDown?.(e);
          return;
        }

        e.preventDefault();
        e.currentTarget.setPointerCapture?.(e.pointerId);

        isPointerDownRef.current = true;
        startedByHoldRef.current = false;

        clearHoldTimer();
        holdTimerRef.current = window.setTimeout(() => {
          // hold threshold reached
          if (!isPointerDownRef.current) return;
          if (voiceMode !== "idle") return;

          startedByHoldRef.current = true;
          onVoiceStart?.(true);
        }, holdThresholdMs);
      },
      [
        disabled,
        isProcessing,
        enableHoldToRecord,
        holdThresholdMs,
        voiceMode,
        onVoiceStart,
        onMicPointerDown,
      ]
    );

    const handleMicPointerUpInternal = useCallback(
      (e: React.PointerEvent) => {
        if (disabled || isProcessing) return;
        if (!enableHoldToRecord) {
          onMicPointerUp?.(e);
          return;
        }

        e.preventDefault();

        isPointerDownRef.current = false;
        clearHoldTimer();

        // If hold started recording, release stops
        if (startedByHoldRef.current) {
          startedByHoldRef.current = false;
          onVoiceStop?.();
          return;
        }

        // Tap behavior: toggle start/stop
        if (voiceMode === "idle") {
          onVoiceStart?.(false);
        } else if (voiceMode === "recording") {
          onVoiceStop?.();
        }
      },
      [
        disabled,
        isProcessing,
        enableHoldToRecord,
        voiceMode,
        onVoiceStart,
        onVoiceStop,
        onMicPointerUp,
      ]
    );

    const handleMicPointerLeaveInternal = useCallback(
      (e: React.PointerEvent) => {
        if (!enableHoldToRecord) {
          onMicPointerLeave?.(e);
          return;
        }

        // Optional: if you want “drag away cancels” only after hold started
        if (startedByHoldRef.current && voiceMode === "recording") {
          e.preventDefault();
          startedByHoldRef.current = false;
          isPointerDownRef.current = false;
          clearHoldTimer();
          onVoiceCancel?.();
        }
      },
      [enableHoldToRecord, voiceMode, onVoiceCancel, onMicPointerLeave]
    );

    // Subscribe to format changes for rich type
    // Depends on editorReady to re-run when ReactQuill loads asynchronously
    useEffect(() => {
      if (type !== "rich" || !editorReady) return;

      const editor = quillRef.current?.getEditor();
      if (!editor) return;

      const updateFormats = () => {
        try {
          setFormats(editor.getFormat());
        } catch (error) {
          // Silent error handling
        }
      };

      editor.on("selection-change", updateFormats);
      editor.on("text-change", updateFormats);
      updateFormats();

      return () => {
        try {
          editor.off("selection-change", updateFormats);
          editor.off("text-change", updateFormats);
        } catch (error) {
          console.error("Error unsubscribing from formats:", error);
        }
      };
    }, [type, editorReady]);

    useEffect(() => {
      if (type !== "text" || !textInputRef.current) return;
      const textarea = textInputRef.current;
      const val = value || "";

      if (val.trim() === "") {
        textarea.style.height = "32px";
        previousHeightRef.current = 32;
        return;
      }

      const computedStyle = window.getComputedStyle(textarea);
      const measure = document.createElement("div");
      measure.style.position = "absolute";
      measure.style.visibility = "hidden";
      measure.style.height = "auto";
      measure.style.width = `${textarea.clientWidth}px`;
      measure.style.whiteSpace = "pre-wrap";
      measure.style.wordWrap = "break-word";
      measure.style.font = computedStyle.font;
      measure.style.fontSize = computedStyle.fontSize;
      measure.style.fontFamily = computedStyle.fontFamily;
      measure.style.lineHeight = computedStyle.lineHeight;
      measure.style.padding = computedStyle.padding;
      measure.style.boxSizing = computedStyle.boxSizing;
      measure.style.border = computedStyle.border;
      measure.textContent = val;

      document.body.appendChild(measure);
      const measuredHeight = measure.offsetHeight;
      document.body.removeChild(measure);

      const finalHeight = Math.min(Math.max(measuredHeight, 32), 122);
      textarea.style.height = finalHeight + "px";
      previousHeightRef.current = finalHeight;
    }, [value, type]);

    const getInputProps = () => {
      const baseProps = {
        placeholder,
        disabled,
        onClick,
        minRows: 1,
        maxRows: 4,
      };

      switch (keyboardType) {
        case "numeric":
          return {
            ...baseProps,
            inputMode: "numeric" as const,
            pattern: "[0-9]*",
          };

        default:
          return {
            ...baseProps,
            inputMode: "text" as const,
            type: "text" as const,
          };
      }
    };

    const renderLeftButton = () => {
      if (showClearButton || hasContent) {
        return (
          <FlexColumn
            alignItems="center"
            justifyContent="center"
            onClick={handleClear}
            data-cy="clear-button"
            height="32px"
            width="32px"
            p={6}
            onMouseDown={(e) => e.preventDefault()}
            onTouchStart={(e) => e.preventDefault()}
          >
            <Icon
              name="fa-sharp fa-solid fa-circle-xmark"
              size="16"
              height="20"
              width="20"
              color={colorPalette.popBlack[200]}
            />
          </FlexColumn>
        );
      }

      if (showPlusButton) {
        return (
          <FlexColumn
            alignItems="center"
            justifyContent="center"
            onClick={handlePlusClick}
            data-cy="plus-button"
            height="32px"
            width="32px"
            p={6}
            className="collab-task-list-icon"
          >
            <Icon
              name="fa-solid fa-list-check"
              size="16"
              height="20"
              width="20"
              color={colorPalette.popBlack[200]}
              state={showPlusNotification ? "notification" : "default"}
              notificationColor={colorPalette.popOrange[500]}
            />
          </FlexColumn>
        );
      }

      return null;
    };

    const renderInput = () => {
      if (type === "text") {
        const showVoiceTopRow = voiceMode !== "idle";

        return (
          <FlexColumn>
            {showVoiceTopRow ? (
              <FlexRow px={10} pt={8} pb={8} width="100%">
                <Box flex={1} width="100%">
                  {isVoiceRecording ? (
                    <AudioRecorderWaveform
                      levels={voiceUiState?.levels ?? []}
                      elapsedSec={voiceUiState?.elapsedSec ?? 0}
                      windowSize={70}
                      minBarPct={6}
                    />
                  ) : isVoiceReview ? (
                    voiceUiState?.blobUrl ? (
                      <AudioPlayer
                        src={voiceUiState.blobUrl}
                        groupId={voiceAudioGroupId}
                        playerId={voicePlayerId}
                        timeMode="current"
                        onPlay={() => onVoicePreviewPlay?.()}
                        onPause={() => onVoicePreviewPause?.()}
                      />
                    ) : null
                  ) : null}
                </Box>
              </FlexRow>
            ) : null}

            {/* Bottom bar row (always mounted) */}
            <FlexRow gap={8} alignItems="flex-end" px={10} pt={8} pb={16}>
              {/* Left */}
              {voiceMode === "idle" ? (
                renderLeftButton()
              ) : (
                <FlexColumn
                  alignItems="center"
                  justifyContent="center"
                  onClick={() => onVoiceCancel?.()}
                  data-cy="voice-cancel-button"
                  height="32px"
                  width="32px"
                  p={6}
                >
                  <Icon
                    name="fa-sharp fa-solid fa-circle-xmark"
                    size="16"
                    height="20"
                    width="20"
                    color={colorPalette.popBlack[200]}
                  />
                </FlexColumn>
              )}

              {/* Middle */}
              <Box flex={1} width="100%">
                {voiceMode === "idle" ? (
                  <StyledInput
                    ref={textInputRef}
                    value={value}
                    onChange={handleTextChange}
                    keyboardType={keyboardType}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    {...getInputProps()}
                  />
                ) : voiceUiState?.limitReached && isVoiceReview ? (
                  <Box
                    height="32px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Body5RegularTypography
                      color="text.neutral.tertiary"
                      textAlign="center"
                    >
                      Message limit reached
                    </Body5RegularTypography>
                  </Box>
                ) : (
                  <Box height="32px" />
                )}
              </Box>

              {/* Right */}
              {voiceMode === "review" ? (
                <FlexColumn
                  alignItems="center"
                  justifyContent="center"
                  height="32px"
                  width="32px"
                  px={4}
                  py={2}
                  borderRadius="8px"
                  flexShrink={0}
                  cursor="pointer"
                  backgroundColor={colorPalette.popOrange[500]}
                  data-cy="voice-send-button"
                  onClick={() => onVoiceSend?.()}
                >
                  <Icon
                    name="fa-kit fa-small-arrow fa-rotate-180"
                    height="8"
                    width="12"
                    color={colorPalette.popWhite[500]}
                    size="8"
                  />
                </FlexColumn>
              ) : rightAction === "mic" || voiceMode === "recording" ? (
                <MicWrapper
                  alignItems="center"
                  justifyContent="center"
                  onClick={enableHoldToRecord ? undefined : handleMicClick}
                  onPointerDown={handleMicPointerDownInternal}
                  onPointerUp={handleMicPointerUpInternal}
                  onPointerLeave={handleMicPointerLeaveInternal}
                  p={6}
                  flexShrink={0}
                  cursor={disabled || isProcessing ? "default" : "pointer"}
                  data-cy="mic-button"
                  width="unset"
                  onContextMenu={(e: React.MouseEvent) => e.preventDefault()}
                >
                  <Icon
                    name={
                      voiceMode === "recording"
                        ? // icon logic fixed in section (2)
                          startedByHoldRef.current
                          ? "fa-solid fa-microphone"
                          : "fa-solid fa-circle-stop"
                        : "fa-solid fa-microphone"
                    }
                    height="20"
                    width="20"
                    color={
                      voiceMode === "recording"
                        ? "text.wishlink.default"
                        : colorPalette.popBlack[500]
                    }
                    size="18"
                    cursor="pointer"
                  />
                </MicWrapper>
              ) : (
                <FlexColumn
                  alignItems="center"
                  justifyContent="center"
                  onClick={handleSubmit}
                  height="32px"
                  width="32px"
                  px={4}
                  py={2}
                  borderRadius="8px"
                  flexShrink={0}
                  cursor={
                    isSubmitDisabled || isProcessing ? "default" : "pointer"
                  }
                  backgroundColor={
                    isSubmitDisabled || isProcessing
                      ? colorPalette.popBlack[100]
                      : colorPalette.popOrange[500]
                  }
                  data-cy="submit-button"
                >
                  {isProcessing ? (
                    <Icon
                      name="fa-solid fa-loader fa-spin"
                      height="10"
                      width="12"
                      color={colorPalette.popWhite[500]}
                      size="10"
                    />
                  ) : (
                    <Icon
                      name="fa-kit fa-small-arrow fa-rotate-180"
                      height="8"
                      width="12"
                      color={colorPalette.popWhite[500]}
                      size="8"
                    />
                  )}
                  {/* keep your existing submit icon logic here */}
                </FlexColumn>
              )}
            </FlexRow>
          </FlexColumn>
        );
      }

      // Rich text editor
      return (
        <FlexColumn>
          <FlexRow gap={8} alignItems="flex-end" px={10} pt={8} pb={16}>
            {renderLeftButton()}

            <StyledQuill
              onClick={onClick}
              onTouchStart={(e) => {
                const quillEditor = quillRef.current?.getEditor()?.root;
                if (quillEditor) {
                  const touchY = e.touches[0].clientY;
                  touchStartYRef.current = touchY;
                  touchStartScrollTopRef.current = quillEditor.scrollTop;
                  isScrollingInputRef.current = false;
                }
              }}
              onTouchMove={(e) => {
                const quillEditor = quillRef.current?.getEditor()?.root;
                if (quillEditor) {
                  const touchY = e.touches[0].clientY;
                  const deltaY = touchY - touchStartYRef.current;
                  const currentScrollTop = quillEditor.scrollTop;
                  const scrollHeight = quillEditor.scrollHeight;
                  const clientHeight = quillEditor.clientHeight;
                  const isAtTop = currentScrollTop === 0;
                  const isAtBottom =
                    currentScrollTop + clientHeight >= scrollHeight - 1;

                  if ((deltaY > 0 && !isAtTop) || (deltaY < 0 && !isAtBottom)) {
                    isScrollingInputRef.current = true;
                    e.stopPropagation();
                  }
                }
              }}
              onTouchEnd={(e) => {
                if (isScrollingInputRef.current) {
                  e.stopPropagation();
                }
                isScrollingInputRef.current = false;
              }}
            >
              <Suspense fallback={<div style={{ minHeight: "32px" }} />}>
                <ReactQuill
                  ref={quillCallbackRef}
                  value={value}
                  onChange={handleQuillChange}
                  placeholder={placeholder}
                  readOnly={disabled}
                  modules={{
                    toolbar: false,
                    clipboard: {
                      matchVisual: false,
                    },
                  }}
                  formats={[
                    "bold",
                    "italic",
                    "underline",
                    "strike",
                    "list",
                    "bullet",
                    "indent",
                    "link",
                    "align",
                    "direction",
                    "code",
                    "code-block",
                  ]}
                  style={{ display: "block", width: "100%" }}
                />
              </Suspense>
            </StyledQuill>

            <FlexColumn
              alignItems="center"
              justifyContent="center"
              onClick={handleSubmit}
              height="32px"
              width="32px"
              px={4}
              py={2}
              flexShrink={0}
              borderRadius="8px"
              cursor={isSubmitDisabled || isProcessing ? "default" : "pointer"}
              backgroundColor={
                isSubmitDisabled || isProcessing
                  ? colorPalette.popBlack[100]
                  : colorPalette.popOrange[500]
              }
              data-cy="submit-button"
            >
              {isProcessing ? (
                <Icon
                  name="fa-solid fa-loader fa-spin"
                  height="10"
                  width="12"
                  color={colorPalette.popWhite[500]}
                  size="10"
                />
              ) : (
                <Icon
                  name="fa-kit fa-small-arrow fa-rotate-180"
                  height="8"
                  width="12"
                  color={colorPalette.popWhite[500]}
                  size="8"
                />
              )}
            </FlexColumn>
          </FlexRow>

          {showToolbar && (
            <FlexRow px={10} py={7} gap={8} backgroundColor="#D3D6DC">
              <FlexColumn
                alignItems="center"
                justifyContent="center"
                p={6}
                height="32px"
                width="32px"
                borderRadius="4px"
                cursor="pointer"
                backgroundColor={
                  isFormatActive("bold")
                    ? colorPalette.info[200]
                    : "transparent"
                }
                onClick={() => handleFormat("bold")}
              >
                <Icon
                  name="fa-solid fa-bold"
                  size="16"
                  height="20"
                  width="20"
                  color={
                    isFormatActive("bold")
                      ? colorPalette.popWhite[500]
                      : colorPalette.popBlack[100]
                  }
                  cursor="pointer"
                />
              </FlexColumn>

              <FlexColumn
                alignItems="center"
                justifyContent="center"
                p={6}
                height="32px"
                width="32px"
                borderRadius="4px"
                cursor="pointer"
                backgroundColor={
                  isFormatActive("italic")
                    ? colorPalette.info[200]
                    : "transparent"
                }
                onClick={() => handleFormat("italic")}
              >
                <Icon
                  name="fa-solid fa-italic"
                  size="16"
                  height="20"
                  width="20"
                  color={
                    isFormatActive("italic")
                      ? colorPalette.popWhite[500]
                      : colorPalette.popBlack[100]
                  }
                  cursor="pointer"
                />
              </FlexColumn>

              <FlexColumn
                alignItems="center"
                justifyContent="center"
                p={6}
                height="32px"
                width="32px"
                borderRadius="4px"
                cursor="pointer"
                backgroundColor={
                  isFormatActive("underline")
                    ? colorPalette.info[200]
                    : "transparent"
                }
                onClick={() => handleFormat("underline")}
              >
                <Icon
                  name="fa-solid fa-underline"
                  size="16"
                  height="20"
                  width="20"
                  color={
                    isFormatActive("underline")
                      ? colorPalette.popWhite[500]
                      : colorPalette.popBlack[100]
                  }
                />
              </FlexColumn>

              <FlexColumn
                alignItems="center"
                justifyContent="center"
                p={6}
                height="32px"
                width="32px"
                borderRadius="4px"
                cursor="pointer"
                backgroundColor={
                  isFormatActive("strike")
                    ? colorPalette.info[200]
                    : "transparent"
                }
                onClick={() => handleFormat("strike")}
              >
                <Icon
                  name="fa-solid fa-strikethrough"
                  size="16"
                  height="20"
                  width="20"
                  color={
                    isFormatActive("strike")
                      ? colorPalette.popWhite[500]
                      : colorPalette.popBlack[100]
                  }
                />
              </FlexColumn>

              <ToolbarSeparator>|</ToolbarSeparator>

              <FlexColumn
                alignItems="center"
                justifyContent="center"
                p={6}
                height="32px"
                width="32px"
                borderRadius="4px"
                cursor="pointer"
                backgroundColor={
                  isListActive("bullet")
                    ? colorPalette.info[200]
                    : "transparent"
                }
                onClick={() => handleList("bullet")}
              >
                <Icon
                  name="fa-solid fa-list-ul"
                  size="16"
                  height="20"
                  width="20"
                  color={
                    isListActive("bullet")
                      ? colorPalette.popWhite[500]
                      : colorPalette.popBlack[100]
                  }
                />
              </FlexColumn>

              <FlexColumn
                alignItems="center"
                justifyContent="center"
                p={6}
                height="32px"
                width="32px"
                borderRadius="4px"
                cursor="pointer"
                backgroundColor={
                  isListActive("ordered")
                    ? colorPalette.info[200]
                    : "transparent"
                }
                onClick={() => handleList("ordered")}
              >
                <Icon
                  name="fa-solid fa-list-ol"
                  size="16"
                  height="20"
                  width="20"
                  color={
                    isListActive("ordered")
                      ? colorPalette.popWhite[500]
                      : colorPalette.popBlack[100]
                  }
                />
              </FlexColumn>

              <FlexColumn
                alignItems="center"
                justifyContent="center"
                p={6}
                height="32px"
                width="32px"
                borderRadius="4px"
                cursor="pointer"
                backgroundColor={
                  isListActive("checked") || isListActive("unchecked")
                    ? colorPalette.info[200]
                    : "transparent"
                }
                onClick={() => handleList("checked")}
              >
                <Icon
                  name="fa-solid fa-tasks"
                  size="16"
                  height="20"
                  width="20"
                  color={
                    isListActive("checked") || isListActive("unchecked")
                      ? colorPalette.popWhite[500]
                      : colorPalette.popBlack[100]
                  }
                />
              </FlexColumn>
            </FlexRow>
          )}
        </FlexColumn>
      );
    };

    return (
      <FlexColumn
        width="100%"
        data-cy={dataCy}
        gap={8}
        backgroundColor={colorPalette.popWhite[300]}
      >
        {renderInput()}
      </FlexColumn>
    );
  }
);
